<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card dark outlined>
          <v-card-title class="secondary py-1 text-subtitle-1">
            <v-icon left dense> mdi-cogs </v-icon>
            Devices
            <v-spacer />
            <v-btn
              v-if="canManageDevices"
              x-small
              dark
              class="mr-2"
              @click="deleteAllDevices()"
            >
              <v-icon left x-small> mdi-delete </v-icon>
              Delete ALL Devices
            </v-btn>
            <v-btn
              v-if="canViewDevices"
              x-small
              dark
              class="mr-2"
              @click="exportDevices"
            >
              <v-icon left x-small> mdi-download </v-icon>
              Export Devices
            </v-btn>
            <v-btn v-if="canManageDevices" x-small dark @click="importDevices">
              <v-icon left x-small> mdi-plus </v-icon>
              Add Devices
            </v-btn>
            <v-dialog v-model="dialogDeleteItem" max-width="520px" dark>
              <v-card>
                <v-card-title class="text-h6 text-wrap justify-center"
                  >Are you sure you want to delete this device?</v-card-title
                >
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="closeDelete">Cancel</v-btn>
                  <v-btn color="red darken-1" text @click="deleteItemConfirmed"
                    >OK</v-btn
                  >
                  <v-spacer></v-spacer>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog v-model="dialogDeleteAllItems" max-width="520px" dark>
              <v-card>
                <v-card-title class="text-h6 text-wrap justify-center"
                  >Are you sure you want to delete ALL items for this event?
                  This cannot be undone.</v-card-title
                >
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="closeDelete">Cancel</v-btn>
                  <v-btn
                    color="red darken-1"
                    text
                    @click="deleteAllItemsConfirmed"
                    >OK</v-btn
                  >
                  <v-spacer></v-spacer>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-data-table
              :headers="headers"
              :items="selectedDeviceProfiles"
              sort-by="StallholderId"
              single-select
              return-object
              class="elevation-5"
            >
              <template v-slot:[`item.StallholderName`]="{ item }">
                {{ displayStallholderName(item) }}
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <template v-if="canManageDevices">
                  <v-icon small class="mr-2" @click="editItem(item)">
                    mdi-pencil
                  </v-icon>
                  <v-icon small @click="deleteItem(item)"> mdi-delete </v-icon>
                </template>
              </template>

              <template v-slot:top>
                <v-toolbar flat>
                  <v-row>
                    <v-col cols="4">
                      <v-combobox
                        filled
                        dense
                        outlined
                        hide-details
                        v-model="selectedStallholderFilter"
                        :items="stallholderFilterList"
                        label="Filter by Stallholder"
                        item-text="StallholderName"
                        item-value="StallholderId"
                        return-object
                        clearable
                      >
                      </v-combobox>
                    </v-col>
                  </v-row>
                </v-toolbar>
              </template>

              <template v-slot:no-data>
                <v-btn color="primary">
                  No devices available for this filter
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import "izitoast/dist/css/iziToast.min.css";
import iziToast from "izitoast/dist/js/iziToast.min.js";
import { mapState, mapActions, mapMutations } from "vuex";
import {
  hasWrite,
  hasReadOrWrite,
  hasEventOrStallholderReadOrWrite,
} from "@/utils/permissions";

const ALL_STALLHOLDERS_OPTION = {
  StallholderId: "",
  StallholderName: "All Devices",
};

const UNLINKED_DEVICES_OPTION = {
  StallholderId: "__UNLINKED__",
  StallholderName: "Unlinked Devices",
};

function isBlankLinkId(value) {
  if (value == null) return true;
  const text = String(value).trim();
  return text === "" || text === "None Selected";
}

export default {
  data: () => ({
    dialog: false,
    dialogDeleteItem: false,
    dialogDeleteAllItems: false,

    allDevices: [],
    selectedStallholderFilter: { ...ALL_STALLHOLDERS_OPTION },
    deleteItemId: "",

    editedIndex: -1,
    editedItem: {
      StallholderId: "",
      StallId: "",
      TerminalUserId: "",
      TerminalId: "",
      DeviceId: "",
    },
    defaultItem: {
      StallholderId: "",
      StallId: "",
      TerminalUserId: "",
      TerminalId: "",
      DeviceId: "",
    },

    valid: true,
  }),

  computed: {
    ...mapState("filter", ["globalEventFilter", "IsAdmin"]),
    ...mapState("admin", [
      "currentEvent",
      "currentStallHolders",
      "currentStallholder",
    ]),

    eventPermission() {
      return this.globalEventFilter?.Permission || "None";
    },

    stallholderPermission() {
      return this.currentStallholder?.Permission || "None";
    },

    canViewDevices() {
      return (
        this.IsAdmin ||
        hasEventOrStallholderReadOrWrite(
          this.eventPermission,
          this.stallholderPermission
        )
      );
    },

    // Mutate only with event-level Write or TSG admin — not stallholder Write
    canManageDevices() {
      return this.IsAdmin || hasWrite(this.eventPermission);
    },

    canSeeUnlinkedDevices() {
      return this.IsAdmin || hasReadOrWrite(this.eventPermission);
    },

    visibleStallholders() {
      const list = this.currentStallHolders || [];
      if (this.IsAdmin || hasReadOrWrite(this.eventPermission)) {
        return list;
      }
      return list.filter((sh) => hasReadOrWrite(sh.Permission));
    },

    stallholderFilterList() {
      const options = [ALL_STALLHOLDERS_OPTION, ...this.visibleStallholders];
      if (this.canSeeUnlinkedDevices) {
        options.push(UNLINKED_DEVICES_OPTION);
      }
      return options;
    },

    headers() {
      const cols = [
        { text: "Stallholder Name", value: "StallholderName" },
        { text: "Stall Name", value: "StallName", align: "center" },
        { text: "Device Id", value: "DeviceId", align: "center" },
        { text: "CAIC", value: "MerchantId", align: "center" },
        { text: "Terminal Number", value: "TerminalNumber", align: "center" },
        { text: "User Name", value: "TerminalUserName", align: "center" },
        { text: "Authorised", value: "Authorised", align: "center" },
      ];
      if (this.canManageDevices) {
        cols.push({ text: "Actions", value: "actions", sortable: false });
      }
      return cols;
    },
    formTitle() {
      return this.editedIndex === -1 ? "New Device" : "Edit Device";
    },

    filteredDevices() {
      const devices = this.allDevices || [];
      const filter = this.selectedStallholderFilter;
      if (!filter || filter.StallholderId === "") {
        return devices;
      }
      if (filter.StallholderId === UNLINKED_DEVICES_OPTION.StallholderId) {
        return devices.filter((device) => this.isDeviceUnlinked(device));
      }
      return devices.filter(
        (device) => device.StallholderId === filter.StallholderId
      );
    },

    selectedDeviceProfiles() {
      return this.canViewDevices ? this.filteredDevices : [];
    },
  },

  mounted() {
    this.getAllDevices();
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDeleteItem(val) {
      val || this.closeDelete();
    },

    currentEvent: {
      handler(newEvent) {
        if (newEvent?.EventId) {
          this.selectedStallholderFilter = { ...ALL_STALLHOLDERS_OPTION };
          this.getAllDevices();
        }
      },
      immediate: true,
    },

    canViewDevices(canView) {
      if (canView) {
        this.getAllDevices();
      } else {
        this.allDevices = [];
      }
    },
  },

  methods: {
    ...mapMutations("admin", ["setCurrentStallholder"]),
    ...mapActions("admin", ["getDevices", "postDevices", "getEventMetadata"]),

    isDeviceUnlinked(device) {
      return (
        isBlankLinkId(device.StallholderId) &&
        isBlankLinkId(device.StallId) &&
        isBlankLinkId(device.TerminalId) &&
        isBlankLinkId(device.UserId) &&
        isBlankLinkId(device.TerminalUserId)
      );
    },

    displayStallholderName(device) {
      const name = device?.StallholderName;
      if (isBlankLinkId(device?.StallholderId) || isBlankLinkId(name)) {
        return "";
      }
      return name;
    },

    async getAllDevices() {
      if (!this.currentEvent?.EventId) return;
      if (!this.canViewDevices) {
        this.allDevices = [];
        return;
      }

      try {
        const paramObj = { EventId: this.currentEvent.EventId };
        const response = await this.getDevices(paramObj);

        // Process the response to normalize UserName field
        response.forEach((device, index, devices) => {
          if ("UserName" in device) {
            devices[index].TerminalUserName = device.UserName;
            devices[index].TerminalUserId = device.UserId;
          }
        });

        this.allDevices = response;
      } catch (error) {
        console.error("Error fetching devices:", error);
        this.allDevices = [];
        iziToast.warning({
          title: "Devices",
          message: "Failed to load devices!",
          position: "topRight",
        });
      }
    },

    editItem(item) {
      if (!this.canManageDevices) return;
      this.$router.push({
        name: "Device Import",
        params: {
          id: item.DeviceId,
          deviceData: item,
          stallholderData: this.currentStallholder,
        },
      });
    },

    deleteItem(item) {
      if (!this.canManageDevices) return;
      console.log("deleteItem (item)", item);
      this.deleteItemId = item.DeviceId;
      this.dialogDeleteItem = true;
    },

    async deleteItemConfirmed() {
      if (!this.canManageDevices) return;
      console.log("deleteItemConfirmed (item)", this.deleteItemId);

      try {
        if (this.deleteItemId) {
          let deletedItem = {
            Type: "Device",
            Action: "delete",
            EventId: this.currentEvent.EventId,
            Devices: [{ DeviceId: this.deleteItemId }],
          };

          console.log("DELETE: ", deletedItem);

          await this.postDevices(deletedItem);

          iziToast.success({
            title: "Device",
            message: "Successfully deleted the Device!",
            position: "topRight",
          });

          this.getAllDevices();
        }
      } catch (e) {
        console.log(e);
        iziToast.warning({
          title: "Device",
          message: "Failed to delete record!",
          position: "center",
        });
      }

      this.closeDelete();
    },

    deleteAllDevices() {
      if (!this.canManageDevices) return;
      this.dialogDeleteAllItems = true;
    },

    async deleteAllItemsConfirmed() {
      if (!this.canManageDevices) return;
      try {
        let deletedMetaData = {
          Type: "Device",
          Action: "deleteall",
          EventId: this.currentEvent.EventId,
        };

        await this.postDevices(deletedMetaData);

        iziToast.success({
          title: "Device",
          message: "Successfully deleted all Devices!",
          position: "topRight",
        });

        this.getAllDevices();
      } catch (err) {
        console.log("ERROR: ", err);

        iziToast.warning({
          title: "Device",
          message: "Failed to delete!",
          position: "center",
        });
      }

      this.closeDelete();
    },

    close() {
      this.dialog = false;

      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    closeDelete() {
      this.dialogDeleteItem = false;
      this.dialogDeleteAllItems = false;
      this.deleteItemId = "";
    },

    importDevices() {
      if (!this.canManageDevices) return;
      this.$router.push({
        name: "Device Import",
        params: {
          id: 0,
          stallholderData: this.currentStallholder,
        },
      });
    },

    exportDevices() {
      const headers = [
        "DeviceID",
        "CAIC",
        "Authorised",
        "Stallholder",
        "Stall",
        "Terminal",
        "User",
      ];

      const data = this.selectedDeviceProfiles.map((device) => [
        device.DeviceId || "",
        device.MerchantId || "",
        device.Authorised || "",
        this.displayStallholderName(device),
        device.StallName || "",
        device.TerminalNumber || "",
        device.TerminalUserName || "",
      ]);

      const csvContent = [
        headers.join(","),
        ...data.map((row) => row.join(",")),
      ].join("\n");

      const eventName = this.currentEvent?.EventName || "Event";
      const stallholderName =
        this.selectedStallholderFilter?.StallholderName || "All";
      const filename = `${eventName}-${stallholderName}-devices.csv`;

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>

<style></style>
